import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, AlertCircle, CheckCircle2, Database } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

interface ReindexResponse {
  status: string;
  pages_indexed: number;
  attachments_indexed: number;
  total_chunks: number;
  customers_total: number;
  orphan_chunks_deleted: number;
  fts_orphan_chunks_deleted?: number;
  unknown_books: Array<{ book_id: number; book_title: string }>;
}

interface FtsResyncResponse {
  status: string;
  fts_removed: number;
  fts_remaining: number;
  chroma_count: number;
  in_sync: boolean;
  missing_in_fts: number;
}

/** 수동 재인덱싱 트리거 + 책 매핑 새로고침 + FTS 동기화 */
export function ReindexButton() {
  const queryClient = useQueryClient();
  const [lastResult, setLastResult] = useState<ReindexResponse | null>(null);
  const [ftsResult, setFtsResult] = useState<FtsResyncResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reindexMutation = useMutation({
    mutationFn: () => api.post<ReindexResponse>("/admin/reindex", {}, { page_limit: 1000 }),
    onSuccess: (data) => {
      setLastResult(data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "index-status"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "unknown-books"] });
    },
    onError: (err: any) => {
      setError(err?.message || "재인덱싱 실패");
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => api.post("/admin/projects/refresh", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "index-status"] });
    },
  });

  // v1.4-c.3: FTS 동기화 (재인덱싱 없이)
  const ftsResyncMutation = useMutation({
    mutationFn: () => api.post<FtsResyncResponse>("/admin/fts/resync", {}),
    onSuccess: (data) => {
      setFtsResult(data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "index-status"] });
    },
    onError: (err: any) => {
      setError(err?.message || "FTS 동기화 실패");
    },
  });

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <RefreshCw className="h-4 w-4 text-purple-600" />
          <h3 className="text-sm font-semibold">인덱싱 관리</h3>
        </div>

        <div className="space-y-2">
          <Button
            onClick={() => reindexMutation.mutate()}
            disabled={reindexMutation.isPending}
            className="w-full"
            variant="default"
            size="sm"
          >
            {reindexMutation.isPending ? (
              <>
                <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                재인덱싱 중... (10~30분 소요)
              </>
            ) : (
              <>
                <RefreshCw className="h-3 w-3 mr-2" />
                전체 재인덱싱
              </>
            )}
          </Button>

          <Button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="w-full"
            variant="outline"
            size="sm"
          >
            책 매핑 캐시 새로고침
          </Button>

          {/* v1.4-c.3: FTS 동기화 (재인덱싱 없이 잉여 청크 정리) */}
          <Button
            onClick={() => ftsResyncMutation.mutate()}
            disabled={ftsResyncMutation.isPending}
            className="w-full"
            variant="outline"
            size="sm"
          >
            {ftsResyncMutation.isPending ? (
              <>
                <Database className="h-3 w-3 mr-2 animate-pulse" />
                FTS 동기화 중...
              </>
            ) : (
              <>
                <Database className="h-3 w-3 mr-2" />
                FTS 동기화 (재인덱싱 없이)
              </>
            )}
          </Button>
        </div>

        {ftsResult && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 space-y-0.5">
            <div>
              <CheckCircle2 className="inline h-3 w-3 mr-1" />
              <span className="font-medium">FTS 동기화 완료</span>
            </div>
            <div>정리된 잉여 청크: {ftsResult.fts_removed}</div>
            <div>FTS: {ftsResult.fts_remaining.toLocaleString()} · Chroma: {ftsResult.chroma_count.toLocaleString()}</div>
            {ftsResult.in_sync ? (
              <div className="text-green-700">✓ 완전 동기화</div>
            ) : (
              <div className="text-orange-700">
                ⚠ FTS에 {ftsResult.missing_in_fts}개 부족 → 재인덱싱 권장
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            <AlertCircle className="inline h-3 w-3 mr-1" />
            {error}
          </div>
        )}

        {lastResult && (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700 space-y-0.5">
            <div>
              <CheckCircle2 className="inline h-3 w-3 mr-1" />
              <span className="font-medium">재인덱싱 완료</span>
            </div>
            <div>페이지: {lastResult.pages_indexed} · 첨부: {lastResult.attachments_indexed}</div>
            <div>총 청크: {lastResult.total_chunks.toLocaleString()}</div>
            <div>
              orphan 정리: Chroma {lastResult.orphan_chunks_deleted}
              {lastResult.fts_orphan_chunks_deleted !== undefined && (
                <> · FTS {lastResult.fts_orphan_chunks_deleted}</>
              )}
            </div>
            {lastResult.unknown_books.length > 0 && (
              <div className="text-orange-700">
                ⚠ 미분류 책 {lastResult.unknown_books.length}건
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
